import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { supabase } from '../lib/supabase'
import type { UserRole } from '../types/user'

interface ProtectedRouteProps {
  children: React.ReactNode
  roles?: UserRole[]
  requireNoProfile?: boolean
}

export function getDefaultRoute(role: UserRole): string {
  switch (role) {
    case 'landlord':
    case 'agent':
      return '/dashboard'
    case 'tenant':
      return '/portal'
    case 'contractor':
      return '/jobs'
    case 'admin':
      // `/admin` was deleted with the admin console (migration 449). The role
      // is still a valid profiles.role value — R9.3 governs its access — but it
      // has no UI of its own, so it lands on the landlord dashboard rather
      // than a route that no longer exists.
      return '/dashboard'
    case 'landlord_client':
      return '/client'
  }
}

function SuspendedScreen() {
  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg-surface)' }}>
      <div className="rounded-2xl p-8 max-w-md text-center shadow-sm" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid color-mix(in srgb, var(--color-error, #ef4444) 30%, transparent)' }}>
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-error, #ef4444) 10%, transparent)' }}>
          <svg className="w-8 h-8" style={{ color: 'var(--color-error, #ef4444)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Account Suspended</h1>
        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
          Your account has been suspended. If you believe this is an error, please contact support.
        </p>
        <button
          onClick={handleSignOut}
          className="px-6 py-2 rounded-lg transition"
          style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}
        >
          Sign Out
        </button>
      </div>
    </div>
  )
}

export default function ProtectedRoute({ children, roles, requireNoProfile }: ProtectedRouteProps) {
  const { user, loading: authLoading, refreshSubscription } = useAuth()
  const { profile, loading: profileLoading } = useProfile()
  const location = useLocation()

  // Refresh subscription status on route change (debounced in useAuth)
  useEffect(() => {
    refreshSubscription()
  }, [location.pathname, refreshSubscription])

  // Show loading state while auth or profile is loading
  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-surface)' }}>
        <div style={{ color: 'var(--text-muted)' }}>Loading...</div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) return <Navigate to="/login" replace />

  // Redirect to onboarding if no profile (unless we're checking for no profile)
  if (!profile && !requireNoProfile) return <Navigate to="/onboarding" replace />

  // Check if user is suspended
  if (profile?.is_suspended) {
    return <SuspendedScreen />
  }

  // If we require no profile but user has one AND they have a subscription,
  // redirect to their default route. Users with null subscription_status
  // (abandoned Stripe checkout) are allowed back to complete plan selection.
  if (profile && requireNoProfile && profile.subscription_status !== null) return <Navigate to={getDefaultRoute(profile.role)} replace />

  // Check role access
  if (roles && profile && !roles.includes(profile.role)) {
    return <Navigate to={getDefaultRoute(profile.role)} replace />
  }

  // For landlord/agent: render children regardless of subscription status.
  // Read-only mode is enforced by WriteActionGuard in LandlordLayout,
  // and the SubscriptionBanner shows the upgrade prompt.
  return <>{children}</>
}
